import React from 'react';
import { FiArrowRight, FiCheck, FiX } from 'react-icons/fi';
import AdminLayout from './AdminLayout';
import styles from './AdminGradingSystem.module.css';

const SECTION_SCORES = [
  { name: 'Cognitive & Analytical Skills', maxScore: 100, description: '50 MCQ × 2 points each' },
  { name: 'Personality & Emotional Intelligence', maxScore: 100, description: '50 Likert scale questions (1-5 points)' },
  { name: 'Leadership & Team Management', maxScore: 100, description: '20 Scenario-based questions × 5 points each' },
  { name: 'Technical Case Study', maxScore: 100, description: '5 Analytical questions × 20 points each' },
];

const GRADE_SCALE = [
  { grade: 'A', minScore: '320', maxScore: '400', minPercentage: '80%', maxPercentage: '100%', color: '#1a5c38', description: 'Excellent Performance' },
  { grade: 'B', minScore: '280', maxScore: '319', minPercentage: '70%', maxPercentage: '79%', color: '#27ae60', description: 'Good Performance' },
  { grade: 'C', minScore: '240', maxScore: '279', minPercentage: '60%', maxPercentage: '69%', color: '#e67e22', description: 'Satisfactory Performance' },
  { grade: 'D', minScore: '200', maxScore: '239', minPercentage: '50%', maxPercentage: '59%', color: '#d35400', description: 'Below Average Performance' },
  { grade: 'F', minScore: '0', maxScore: '199', minPercentage: '0%', maxPercentage: '49%', color: '#c0392b', description: 'Failing Performance' },
];

const RECOMMENDATION_THRESHOLDS = [
  { 
    category: 'Advance to Next Stage',
    scoreRange: '280–400 points',
    percentageRange: '70%+',
    color: '#1a5c38',
    icon: FiCheck,
    description: 'Candidate meets the threshold for advancement and demonstrates readiness for the next evaluation stage.'
  },
  { 
    category: 'Conditional Review / Panel Review',
    scoreRange: '240–279 points',
    percentageRange: '60–69%',
    color: '#e67e22',
    icon: FiArrowRight,
    description: 'Candidate partially meets the threshold. Targeted development is recommended. Panel review may be required.'
  },
  { 
    category: 'Do Not Advance',
    scoreRange: '0–239 points',
    percentageRange: 'Below 60%',
    color: '#c0392b',
    icon: FiX,
    description: 'Candidate does not meet the minimum threshold. Reassessment may be considered after a development period.'
  },
];

export default function AdminGradingSystem() {
  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Assessment Grading System</h1>
          <p className={styles.subtitle}>Complete overview of scoring, grading, and recommendation thresholds</p>
        </div>

        {/* Section Scores */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Section Scoring Structure</h2>
            <p className={styles.sectionDesc}>Total Assessment: 400 Points | Duration: 1 Hour 45 Minutes</p>
          </div>
          <div className={styles.sectionScoresGrid}>
            {SECTION_SCORES.map((sec, idx) => (
              <div key={idx} className={styles.scoreCard}>
                <div className={styles.scoreNum}>{idx + 1}</div>
                <div className={styles.scoreBody}>
                  <h3 className={styles.scoreName}>{sec.name}</h3>
                  <p className={styles.scoreDesc}>{sec.description}</p>
                  <div className={styles.scoreValue}>{sec.maxScore} pts</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Grade Scale */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Grade Scale</h2>
            <p className={styles.sectionDesc}>Performance grades based on overall score</p>
          </div>
          <div className={styles.gradeScaleTable}>
            <div className={styles.tableHeader}>
              <div className={styles.colGrade}>Grade</div>
              <div className={styles.colScore}>Score Range</div>
              <div className={styles.colPercent}>Percentage</div>
              <div className={styles.colDesc}>Performance Level</div>
            </div>
            {GRADE_SCALE.map((row, idx) => (
              <div key={idx} className={styles.tableRow}>
                <div className={styles.colGrade}>
                  <div className={styles.gradeBadge} style={{ background: row.color }}>
                    {row.grade}
                  </div>
                </div>
                <div className={styles.colScore}>
                  {row.minScore}–{row.maxScore} pts
                </div>
                <div className={styles.colPercent}>
                  {row.minPercentage}–{row.maxPercentage}
                </div>
                <div className={styles.colDesc}>
                  {row.description}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recommendation Thresholds */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Recommendation Thresholds</h2>
            <p className={styles.sectionDesc}>Advancement decisions based on overall performance</p>
          </div>
          <div className={styles.recommendationsGrid}>
            {RECOMMENDATION_THRESHOLDS.map((rec, idx) => (
              <div 
                key={idx} 
                className={styles.recommendationCard}
                style={{ borderTopColor: rec.color }}
              >
                <div className={styles.recIcon} style={{ background: rec.color }}>
                  <rec.icon aria-hidden="true" />
                </div>
                <div className={styles.recContent}>
                  <h3 className={styles.recCategory}>{rec.category}</h3>
                  <div className={styles.recMetrics}>
                    <div className={styles.metricItem}>
                      <span className={styles.metricLabel}>Score:</span>
                      <span className={styles.metricValue}>{rec.scoreRange}</span>
                    </div>
                    <div className={styles.metricItem}>
                      <span className={styles.metricLabel}>Percentage:</span>
                      <span className={styles.metricValue}>{rec.percentageRange}</span>
                    </div>
                  </div>
                  <p className={styles.recDescription}>{rec.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: Personality & Emotional Intelligence Guidance */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Section 2: Personality & Emotional Intelligence Guidance</h2>
            <p className={styles.sectionDesc}>Eight core competencies with scoring bands and behavioral descriptors</p>
          </div>
          <div className={styles.competencyTable}>
            <div className={styles.tableHeader}>
              <div className={styles.colCluster}>CLUSTER</div>
              <div className={styles.colQuestions}>QUESTIONS</div>
              <div className={styles.colMaxScore}>MAX SCORE</div>
              <div className={styles.colStrong}>75–100% = STRONG</div>
              <div className={styles.colModerate}>55–74% = MODERATE</div>
              <div className={styles.colGap}>BELOW 65% = GAP</div>
              <div className={styles.colMeasures}>WHAT IT MEASURES</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Achievement Drive</div>
              <div className={styles.colQuestions}>Q1–Q7</div>
              <div className={styles.colMaxScore}>35</div>
              <div className={styles.colStrong}>≥26</div>
              <div className={styles.colModerate}>19–25</div>
              <div className={styles.colGap}>&lt;19</div>
              <div className={styles.colMeasures}>Self-motivation, goal orientation, personal performance standards, ambition beyond minimum requirements</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Resilience & Adaptability</div>
              <div className={styles.colQuestions}>Q8–Q14</div>
              <div className={styles.colMaxScore}>35</div>
              <div className={styles.colStrong}>≥26</div>
              <div className={styles.colModerate}>19–25</div>
              <div className={styles.colGap}>&lt;19</div>
              <div className={styles.colMeasures}>Bounce-back ability, handling pressure, managing uncertainty, adapting strategy when needed</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Empathy & Interpersonal</div>
              <div className={styles.colQuestions}>Q15–Q20</div>
              <div className={styles.colMaxScore}>30</div>
              <div className={styles.colStrong}>≥23</div>
              <div className={styles.colModerate}>17–22</div>
              <div className={styles.colGap}>&lt;17</div>
              <div className={styles.colMeasures}>Understanding others, listening quality, communication flexibility, relationship depth</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Team Leadership</div>
              <div className={styles.colQuestions}>Q21–Q27</div>
              <div className={styles.colMaxScore}>35</div>
              <div className={styles.colStrong}>≥26</div>
              <div className={styles.colModerate}>19–25</div>
              <div className={styles.colGap}>&lt;19</div>
              <div className={styles.colMeasures}>Developing others, delegating meaningfully, psychological safety, accountability, purpose clarity</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Analytical Thinking</div>
              <div className={styles.colQuestions}>Q28–Q33</div>
              <div className={styles.colMaxScore}>30</div>
              <div className={styles.colStrong}>≥23</div>
              <div className={styles.colModerate}>17–22</div>
              <div className={styles.colGap}>&lt;17</div>
              <div className={styles.colMeasures}>Data-driven decision-making, root cause analysis, pattern recognition, proactive risk identification</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Initiative & Drive</div>
              <div className={styles.colQuestions}>Q34–Q38</div>
              <div className={styles.colMaxScore}>25</div>
              <div className={styles.colStrong}>≥19</div>
              <div className={styles.colModerate}>14–18</div>
              <div className={styles.colGap}>&lt;14</div>
              <div className={styles.colMeasures}>Proactivity, follow-through, identifying improvement opportunities, acting without being told</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Integrity & Ethics</div>
              <div className={styles.colQuestions}>Q39–Q43</div>
              <div className={styles.colMaxScore}>25</div>
              <div className={styles.colStrong}>≥19</div>
              <div className={styles.colModerate}>14–18</div>
              <div className={styles.colGap}>&lt;14</div>
              <div className={styles.colMeasures}>Honest reporting, consistent standards, transparency, raising ethical concerns, honoring commitments</div>
            </div>

            <div className={styles.tableRow}>
              <div className={styles.colCluster}>Strategic Orientation</div>
              <div className={styles.colQuestions}>Q44–Q50</div>
              <div className={styles.colMaxScore}>35</div>
              <div className={styles.colStrong}>≥26</div>
              <div className={styles.colModerate}>19–25</div>
              <div className={styles.colGap}>&lt;19</div>
              <div className={styles.colMeasures}>Long-term thinking, market awareness, aligning daily actions to commercial objectives, sustainable value creation</div>
            </div>
          </div>
        </section>


      </div>
    </AdminLayout>
  );
}
